import type { ExerciseParameters } from "./api/types";
import { k, t } from "./i18n/i18n";

/** Nom de la rotation CENTAC jouée (demande d'Alexis du 26/10/2026, registre A119) : la mission d'un dossier CENTAC
 *  s'appelle « Rotation NN/AA » — NN, numéro de la rotation dans l'année (01 à 99), AA, l'année sur deux chiffres
 *  (« Rotation 08/26 », même forme en anglais). Le serveur valide la même forme (`centac.rotation_code`). */

const ROTATION_CODE = /^(0[1-9]|[1-9][0-9])\/[0-9]{2}$/;

/** Masque de saisie « NN/AA » : chiffres seuls, quatre au plus, barre oblique posée après le numéro ; « 8/ » devient
 *  « 08/ » (numéro sur un chiffre suivi de la barre). */
export function maskRotation(raw: string): string {
  const single = /^\s*(\d)\s*\/\s*$/.exec(raw);
  if (single) return `0${single[1]}/`;
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  if (digits.length === 2 && /\/\s*$/.test(raw)) return `${digits}/`;
  return digits;
}

/** Numéro complet et valide (« 08/26 »). */
export function isRotationCode(value: string | null | undefined): value is string {
  return typeof value === "string" && ROTATION_CODE.test(value);
}

/** Erreur de saisie (clé du message, à passer à `t`) ; null pour un champ vide ou un numéro valide. */
export function rotationError(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  if (!text || isRotationCode(text)) return null;
  if (/^00\//.test(text)) return k("Le numéro de la rotation va de 01 à 99.");
  return k("Saisissez la rotation sous la forme NN/AA, par exemple 08/26.");
}

/** Numéro de la rotation d'un dossier CENTAC (« 08/26 ») ; null hors CENTAC ou s'il manque. */
export function rotationCode(parameters: Pick<ExerciseParameters, "level" | "centac_rotation"> | null | undefined): string | null {
  if (parameters?.level !== "CENTAC") return null;
  return isRotationCode(parameters.centac_rotation) ? parameters.centac_rotation : null;
}

/** « Rotation 08/26 » ; chaîne vide sans numéro. */
export function rotationName(code: string | null | undefined): string {
  return code ? t("Rotation {code}", { code }) : "";
}

// --- Nom du dossier CENTAC (demande d'Alexis du 26/10/2026, registre A122) ------------------------------------------
// Le nom d'un dossier CENTAC EST celui de la rotation jouée (« Rotation 08/26 ») : jamais saisi à part, il suit le
// numéro (le serveur le tire de `centac_rotation`, `centac.dossier_name`). Sans numéro, un nouveau dossier s'appelle
// « Rotation à numéroter » (le serveur le dit en anglais quand l'anglais est lu).

/** Nom provisoire d'un dossier CENTAC sans numéro (clé du message, celle du serveur). */
export const UNNUMBERED_NAME = k("Rotation à numéroter");

/** Nom d'un dossier CENTAC : « Rotation 08/26 », « Rotation à numéroter » sans numéro valide. */
export function centacDossierName(value: string | null | undefined): string {
  return isRotationCode(value) ? `Rotation ${value}` : t(UNNUMBERED_NAME);
}

/** Nom provisoire (dans une langue ou l'autre) : rien à garder quand le dossier quitte le CENTAC. */
export function isProvisionalName(name: string): boolean {
  return name === UNNUMBERED_NAME || name === t(UNNUMBERED_NAME) || /^Rotation (0[1-9]|[1-9][0-9])\/[0-9]{2}$/.test(name);
}

/** Numéro cité par un nom libre (« 1er BCP Rotation 08-2026 », « ROT 8/26 » → « 08/26 ») ; même règle que le serveur
 *  (`centac.deduced_rotation`). Repris quand on choisit le niveau CENTAC après avoir saisi un nom. */
export function deducedRotation(name: string | null | undefined): string | null {
  const found = /(?<![a-z])rot(?:ation)?\.?[\s_-]*(\d{1,2})[\s_]*[/\-_.][\s_]*(\d{4}|\d{2})(?!\d)/i.exec(name ?? "");
  if (!found) return null;
  const number = Number(found[1]);
  return number >= 1 && number <= 99 ? `${String(number).padStart(2, "0")}/${found[2]!.slice(-2)}` : null;
}

/** Dossier enregistré, vu par les Données initiales. */
type SavedDossier = { name: string; parameters: Pick<ExerciseParameters, "level" | "centac_rotation" | "extra"> } | null | undefined;

/** Nom du brouillon d'un dossier CENTAC, comme le serveur le tirera du numéro saisi : « Rotation 08/26 » ; sans numéro
 *  valide, le nom gardé d'un dossier CENTAC enregistré (dossier d'avant la règle, renommé au numéro), sinon « Rotation
 *  à numéroter ». */
export function centacDraftName(code: string | null | undefined, saved: SavedDossier): string {
  if (isRotationCode(code) || saved?.parameters.level !== "CENTAC") return centacDossierName(code);
  return saved.name;
}

/** Nom du brouillon quand un nouveau dossier quitte le niveau CENTAC : le nom provisoire ne lui reste pas. */
export function nameLeavingCentac(name: string, saved: boolean): string {
  return !saved && isProvisionalName(name) ? "" : name;
}

/** Ligne sous le champ du numéro : l'ancien nom d'un dossier renommé « Rotation NN/AA » (`extra.former_name`), ou le
 *  nom gardé jusqu'au numéro d'un dossier d'avant la règle ; null sinon. */
export function rotationNameNote(saved: SavedDossier): string | null {
  if (!saved || saved.parameters.level !== "CENTAC") return null;
  const former = saved.parameters.extra?.former_name;
  if (typeof former === "string" && former.trim()) return t("Ancien nom : « {name} »", { name: former });
  if (isRotationCode(saved.parameters.centac_rotation) || isProvisionalName(saved.name)) return null;
  return t("Nom actuel « {name} », remplacé par « Rotation NN/AA » une fois le numéro saisi.", { name: saved.name });
}
